import React, { forwardRef, useImperativeHandle, useReducer } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { removeTime } from 'lane-shared/domains/visitorManagement/helpers';
import {
  createScheduleReducer,
  ScheduleActionType,
  ScheduleReducerType,
} from 'lane-shared/domains/visitorManagement/reducers';

import Checkbox from 'components/form/Checkbox';
import { TimePicker, TimeIntervals, S } from 'design-system-web';

import DatePickerButton from '../DatePickers/DatePickerButton';

import styles from './ScheduleDateTime.scss';

export type ScheduleDateTimeHandle = {
  reset: () => void;
};

type Props = {
  className?: string;
  allDayPassEnabled: boolean;
  defaultIsAllDay: boolean;
  defaultStartDate: Date;
  defaultEndDate: Date;
  timeZone?: string;
  minDate?: Date;
  maxDate?: Date;
  onChange: (value: ScheduleReducerType) => void;
  isPreview?: boolean;
};

function ScheduleDateTime(
  {
    className,
    allDayPassEnabled,
    defaultIsAllDay,
    defaultStartDate,
    defaultEndDate,
    timeZone,
    minDate,
    maxDate,
    onChange,
    isPreview = false,
  }: Props,
  ref: any
): React.ReactElement {
  const { t } = useTranslation();

  const reducerFunction = createScheduleReducer(
    {
      isAllDay: defaultIsAllDay,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
    onChange,
    timeZone
  );

  const [state, dispatch] = useReducer(
    reducerFunction,
    {
      isAllDay: defaultIsAllDay,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      internalStartDate: removeTime(defaultStartDate, timeZone),
      internalEndDate: removeTime(defaultEndDate, timeZone),
      startTime: defaultStartDate,
      endTime: defaultEndDate,
    }
  );

  useImperativeHandle(ref, () => {
    return {
      reset: () => {
        dispatch({
          type: ScheduleActionType.Reset,
        });
      },
    };
  });

  return (
    <div className={cx(styles.visitorScheduleDateTime, className)}>
      {allDayPassEnabled && (
        <div className={styles.allDayCheckboxContainer}>
          <Checkbox
            selected={state.isAllDay}
            value={state.isAllDay}
            onChange={() => {
              dispatch({
                type: ScheduleActionType.ToggleAllDay,
              });
            }}
            className={styles.allDayCheckbox}
            labelStyles={{ fontSize: 16, paddingLeft: 7 }}
            text={t('web.content.feature.visitorManagement.form.allDay')}
            testId="allDayCheckbox"
            disabled={isPreview}
          />
        </div>
      )}
      <div className={styles.dateOptions}>
        <div className={styles.dateTimeContainer}>
          <div className={styles.dateTimes}>
            <div className={styles.group}>
              <S data-test="vm-select-date">
                {t('web.content.feature.visitorManagement.form.selectDate')}
              </S>
              <DatePickerButton
                value={state.internalStartDate}
                className={styles.datePicker}
                buttonClassName={styles.datePickerButton}
                onChange={date =>
                  dispatch({
                    type: ScheduleActionType.SetDate,
                    value: { date },
                  })
                }
                hideLabel
                minDate={minDate}
                maxDate={maxDate}
                timeZone={timeZone}
                disabled={isPreview}
                data-test="datePickerButton"
              />
            </div>
          </div>
        </div>
        {(!allDayPassEnabled || !state.isAllDay) && (
          <div className={styles.dateTimeContainer}>
            <div className={styles.dateTimes}>
              <div className={styles.group}>
                <S data-test="vm-start-time">
                  {t(
                    'web.content.feature.visitorManagement.scheduler.form.startTime'
                  )}
                </S>
                <TimePicker
                  hideLabel
                  className={styles.timePicker}
                  buttonClassName={styles.timePickerButton}
                  value={state.startTime}
                  onChange={value => {
                    dispatch({
                      type: ScheduleActionType.SetStartTime,
                      value: {
                        date: new Date(value),
                      },
                    });
                  }}
                  showClear={false}
                  showIcon={false}
                  timeZone={timeZone}
                  unit={TimeIntervals.QUARTER_HOUR}
                  testId="startTime"
                  disabled={isPreview}
                />
              </div>
              <div className={styles.group}>
                <S data-test="vm-end-time">
                  {t(
                    'web.content.feature.visitorManagement.scheduler.form.endTime'
                  )}
                </S>
                <TimePicker
                  hideLabel
                  className={styles.timePicker}
                  buttonClassName={styles.timePickerButton}
                  value={state.endTime}
                  onChange={value => {
                    dispatch({
                      type: ScheduleActionType.SetEndTime,
                      value: {
                        date: new Date(value),
                      },
                    });
                  }}
                  showClear={false}
                  showIcon={false}
                  timeZone={timeZone}
                  unit={TimeIntervals.QUARTER_HOUR}
                  testId="endTime"
                  disabled={isPreview}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ScheduleDateTimeForwarded = forwardRef<ScheduleDateTimeHandle, Props>(
  ScheduleDateTime
);

export default ScheduleDateTimeForwarded;
